import { createContext, useContext, type ComponentType, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Inbox, Info, XOctagon } from "lucide-react";
import { cx } from "../lib/cx";
import { formatMoney, toCents, type CentsLike } from "../lib/money";
import { useProgressSignal } from "../lib/progress";
import { AnimatedValue } from "./AnimatedValue";

// ---------------------------------------------------------------- status

export type Tone = "neutral" | "ok" | "warn" | "crit" | "info" | "accent";

export function StatusPill({ tone = "neutral", children, plain }: { tone?: Tone; children: ReactNode; plain?: boolean }) {
  return <span className={cx("vx-pill", tone !== "neutral" && `vx-pill--${tone}`, plain && "vx-pill--plain")}>{children}</span>;
}

/** Mapa de status → rótulo pt-BR e tom. Cada módulo registra o seu. */
export type StatusMap = Record<string, { label: string; tone: Tone }>;

export function StatusFromMap({ map, value }: { map: StatusMap; value: string | null | undefined }) {
  if (!value) return null;
  const s = map[value] ?? { label: value, tone: "neutral" as Tone };
  return <StatusPill tone={s.tone}>{s.label}</StatusPill>;
}

// ---------------------------------------------------------------- page

export interface Crumb {
  label: string;
  to?: string;
}

/**
 * Contexto da página atual, dado pelo app (painel ou portal): a área do menu em que a tela está. O PageHeader mostra
 * essa área acima do título quando a tela não tem trilha, para o usuário saber onde está sem olhar o menu.
 */
export interface PageContextValue {
  section?: string;
}
const PageCtx = createContext<PageContextValue>({});
export const PageContextProvider = PageCtx.Provider;
export function usePageContext(): PageContextValue {
  return useContext(PageCtx);
}

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  crumbs?: Crumb[];
  meta?: ReactNode;
  /** Linha pequena acima do título; sem ela (e sem trilha) aparece a área do menu (PageContextProvider). */
  eyebrow?: ReactNode;
}

export function PageHeader({ title, description, actions, crumbs, meta, eyebrow }: PageHeaderProps) {
  const ctx = usePageContext();
  const hasCrumbs = Boolean(crumbs && crumbs.length > 0);
  const over = eyebrow ?? (hasCrumbs ? null : ctx.section);
  return (
    <header className="vx-page-header">
      {hasCrumbs ? (
        <nav aria-label="Trilha">
          <ol className="vx-crumbs">
            {crumbs!.map((c, i) => (
              <li key={i}>{c.to ? <Link to={c.to}>{c.label}</Link> : <span aria-current="page">{c.label}</span>}</li>
            ))}
          </ol>
        </nav>
      ) : over ? (
        <p className="vx-eyebrow">{over}</p>
      ) : null}
      <div className="vx-page-head">
        <div className="vx-page-head__text">
          <h1 className="vx-page-title">{title}</h1>
          {meta ? <div className="vx-row vx-page-head__meta">{meta}</div> : null}
          {description ? <p className="vx-page-head__desc">{description}</p> : null}
        </div>
        {actions ? <div className="vx-page-head__actions">{actions}</div> : null}
      </div>
    </header>
  );
}

export function Section({ title, actions, children, id }: { title: ReactNode; actions?: ReactNode; children: ReactNode; id?: string }) {
  return (
    <section className="vx-section" aria-labelledby={id}>
      <div className="vx-section__head">
        <h2 id={id}>{title}</h2>
        <span className="vx-spacer" />
        {actions}
      </div>
      {children}
    </section>
  );
}

export interface PanelProps {
  title?: ReactNode;
  /** Nível do título (padrão 2; 3 quando o painel fica dentro de uma seção com título próprio). */
  level?: 2 | 3;
  actions?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  flush?: boolean;
  className?: string;
  id?: string;
}

export function Panel({ title, actions, children, footer, flush, className, id, level = 2 }: PanelProps) {
  const H = level === 3 ? "h3" : "h2";
  return (
    <section className={cx("vx-panel", className)} aria-labelledby={title && id ? id : undefined}>
      {title || actions ? (
        <div className="vx-panel__head">
          {title ? (
            <H className="vx-panel__title" id={id}>
              {title}
            </H>
          ) : null}
          <span className="vx-spacer" />
          {actions}
        </div>
      ) : null}
      <div className={cx("vx-panel__body", flush && "vx-panel__body--flush")}>{children}</div>
      {footer ? <div className="vx-panel__foot">{footer}</div> : null}
    </section>
  );
}

// ---------------------------------------------------------------- empty / callout

export interface EmptyStateProps {
  title: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  /** Ícone lucide do assunto (padrão: caixa de entrada). `null` tira a ilustração. */
  icon?: ComponentType<{ "aria-hidden"?: boolean | "true" | "false" }> | null;
  /** Título como h1 (telas de status: não encontrada, sem acesso). */
  heading?: boolean;
  /** Compacto: dentro de tabelas e painéis pequenos. */
  compact?: boolean;
}

export function EmptyState({ title, children, actions, icon = Inbox, heading, compact }: EmptyStateProps) {
  const Icon = icon;
  const T = heading ? "h1" : "p";
  return (
    <div className={cx("vx-empty", compact && "vx-empty--compact")}>
      {Icon ? (
        <span className="vx-empty__art" aria-hidden="true">
          <Icon aria-hidden="true" />
        </span>
      ) : null}
      <T className="vx-empty__title">{title}</T>
      {children ? <div className="vx-empty__text">{children}</div> : null}
      {actions ? <div className="vx-empty__actions">{actions}</div> : null}
    </div>
  );
}

const calloutIcon = { info: Info, warn: AlertTriangle, crit: XOctagon, ok: CheckCircle2 } as const;

export function Callout({ tone = "info", title, children }: { tone?: "info" | "warn" | "crit" | "ok"; title?: ReactNode; children?: ReactNode }) {
  const Icon = calloutIcon[tone];
  return (
    <div className={`vx-callout vx-callout--${tone}`} role={tone === "crit" ? "alert" : "status"}>
      <Icon aria-hidden="true" />
      <div>
        {title ? <p className="vx-callout__title">{title}</p> : null}
        {children ? <div>{children}</div> : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- listas

export function DescriptionList({ items }: { items: Array<[ReactNode, ReactNode]> }) {
  return (
    <dl className="vx-dl">
      {items.map(([k, v], i) => (
        <div key={i} style={{ display: "contents" }}>
          <dt>{k}</dt>
          <dd>{v === null || v === undefined || v === "" ? <span className="muted">Não informado</span> : v}</dd>
        </div>
      ))}
    </dl>
  );
}

export interface TimelineItem {
  id: string | number;
  title: ReactNode;
  meta?: ReactNode;
  body?: ReactNode;
  tone?: "accent" | "ok" | "crit" | "neutral";
}

export function Timeline({ items, empty = "Nada registrado ainda." }: { items: TimelineItem[]; empty?: ReactNode }) {
  if (items.length === 0) return <p className="muted">{empty}</p>;
  return (
    <ol className="vx-timeline">
      {items.map((it) => (
        <li key={it.id} className="vx-timeline__item">
          <span className={cx("vx-timeline__dot", it.tone && it.tone !== "neutral" && `vx-timeline__dot--${it.tone}`)} aria-hidden="true" />
          <div>
            <p className="vx-timeline__title">{it.title}</p>
            {it.meta ? <p className="vx-timeline__meta">{it.meta}</p> : null}
            {it.body ? <div className="vx-timeline__body">{it.body}</div> : null}
          </div>
        </li>
      ))}
    </ol>
  );
}

// ---------------------------------------------------------------- números

export function MoneyCell({ value, muteZero, signed }: { value: CentsLike; muteZero?: boolean; signed?: boolean }) {
  const c = toCents(value);
  if (c === null) return <span className="muted">-</span>;
  const cls = cx("num", muteZero && c === 0 && "muted", signed && c < 0 && "text-crit");
  return <span className={cls}>{formatMoney(c)}</span>;
}

export interface KpiTileProps {
  label: ReactNode;
  value: ReactNode;
  foot?: ReactNode;
  tone?: "ok" | "warn" | "crit";
}

export function KpiTile({ label, value, foot, tone }: KpiTileProps) {
  return (
    <div className={cx("vx-kpi", tone && `vx-kpi--${tone}`)}>
      <div className="vx-kpi__label">{label}</div>
      <div className="vx-kpi__value">{typeof value === "string" ? <AnimatedValue text={value} /> : value}</div>
      {foot ? <div className="vx-kpi__foot">{foot}</div> : null}
    </div>
  );
}

export function KpiRow({ children }: { children: ReactNode }) {
  return <div className="vx-grid--kpi vx-grid">{children}</div>;
}

export function Meter({ pct, tone }: { pct: number; tone?: "warn" | "crit" }) {
  const w = Math.max(0, Math.min(100, pct));
  return (
    <div className={cx("vx-meter", tone && `vx-meter--${tone}`)} role="presentation">
      <span style={{ width: `${w}%` }} />
    </div>
  );
}

/** Esqueleto de uma página inteira (título, indicadores e tabela): fallback das telas carregadas sob demanda. */
export function PageSkeleton({ bare }: { bare?: boolean } = {}) {
  // Enquanto a tela carrega, a linha de progresso sob o topo corre.
  useProgressSignal(true);
  return (
    <div className={cx("vx-page-skel", !bare && "vx-content")} aria-busy="true">
      <span className="sr-only" role="status">
        Carregando a página
      </span>
      <span className="vx-skel vx-skel--title" />
      <span className="vx-skel" style={{ width: "42%" }} />
      <div className="vx-page-skel__kpis">
        {[0, 1, 2, 3].map((i) => (
          <span key={i} className="vx-skel vx-skel--block" />
        ))}
      </div>
      <div className="vx-panel vx-page-skel__table">
        {[92, 80, 86, 70, 78].map((w, i) => (
          <span key={i} className="vx-skel" style={{ width: `${w}%` }} />
        ))}
      </div>
    </div>
  );
}

export function Skeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div className="vx-stack" aria-busy="true" aria-live="polite">
      <span className="sr-only">Carregando</span>
      {Array.from({ length: lines }, (_, i) => (
        <span key={i} className="vx-skel" style={{ width: `${90 - i * 12}%` }} />
      ))}
    </div>
  );
}
